import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link2, Loader2, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { formatIDR } from "@/lib/constants";
import SyncStickyTableHead from "@/components/table/SyncStickyTableHead";

const TX_DATE_HINT = "Tanggal Transaksi adalah tanggal terjadinya transaksi pada sumber data (mutasi bank / tanggal aktual item petty cash). Tanggal ini bukan tanggal pelaporan, tanggal input ke sistem, tanggal verifikasi, atau tanggal alokasi ke Cost Control.";

/**
 * KlasifikasiPengeluaranTab — 2026-06-20 (user request). Daftar mutasi
 * "Pembayaran" milik proyek (Kas & Bank → Buku Bank → Pembayaran) + alokasi
 * nominalnya ke baris Sub Item Pekerjaan. Expense item = total alokasi
 * (otomatis — tidak bisa diisi manual).
 */

const parseNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  let s = String(v).trim().replace(/rp/gi, "").replace(/\s/g, "");
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  else if ((s.match(/\./g) || []).length > 1) s = s.replace(/\./g, "");
  const n = Number(s);
  return isNaN(n) ? 0 : n;
};

export default function KlasifikasiPengeluaranTab({ pid, canEdit, wos = [], scope }) {
  const [sources, setSources] = useState(null);
  const [totals, setTotals] = useState(null);
  const [allocs, setAllocs] = useState([]);
  const [scopeItems, setScopeItems] = useState({});
  const [err, setErr] = useState(null);
  const [dlgFor, setDlgFor] = useState(null);
  // Ruang lingkup item: proyek ini + seluruh WO anaknya (project maintenance).
  const scopes = useMemo(() => [{ id: pid, label: null }, ...wos.map((w) => ({ id: w.id, label: w.wo_number || w.project_name || w.id.slice(0, 6) }))], [pid, wos]);

  const load = useCallback(async () => {
    setErr(null);
    try {
      // Konteks eksplisit — backend memvalidasi & menentukan cakupan (tanpa fallback).
      const params = scope ? { scope_type: scope.scope_type, project_id: scope.project_id, work_order_id: scope.work_order_id, variation_order_id: scope.variation_order_id } : {};
      const [s, a, ...ccLists] = await Promise.all([
        api.get(`/active-projects/${pid}/cost-control/expense-sources`, { params }).then((r) => { setTotals(r.data?.totals || null); return r.data?.sources || []; }),
        api.get(`/active-projects/${pid}/cost-control/allocations`).then((r) => r.data?.allocations || []),
        ...scopes.map((sc) => api.get(`/active-projects/${sc.id}/cost-control`).then((r) => r.data?.items || [])),
      ]);
      setSources(s); setAllocs(a);
      setScopeItems(Object.fromEntries(scopes.map((sc, i) => [sc.id, ccLists[i]])));
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat data pengeluaran.");
    }
  }, [pid, scopes, scope]);
  useEffect(() => { load(); }, [load]);

  // Target alokasi: baris pekerjaan (Sub/polos) + Overhead Cost (2026-10-14 user).
  const groups = useMemo(() => {
    const out = [];
    for (const sc of scopes) {
      let cur = null;
      const list = scopeItems[sc.id] || [];
      // 1. Grup pekerjaan (hierarki no/sub)
      for (const it of list.filter((i) => i.section === "pekerjaan")) {
        if (it.no) { cur = { leader: it, subs: [], scopeLabel: sc.label }; out.push(cur); }
        else {
          if (!cur) { cur = { leader: null, subs: [], scopeLabel: sc.label }; out.push(cur); }
          cur.subs.push(it);
        }
      }
      // 2. Grup Overhead Cost — judul buatan supaya tetap muncul sebagai opsi target.
      const overheadRows = list.filter((i) => i.section === "overhead");
      if (overheadRows.length) {
        out.push({ leader: { id: `__oh-${sc.id}`, no: "OH", description: "Overhead Cost", __virtual: true }, subs: overheadRows, scopeLabel: sc.label });
      }
    }
    return out;
  }, [scopes, scopeItems]);
  const subCount = useMemo(() => groups.reduce((a, g) => a + g.subs.length, 0), [groups]);

  if (err) return <div className="p-4 text-sm text-rose-700" data-testid="cc-exp-error">{err}</div>;
  if (sources === null) return <div className="p-6 text-sm text-slate-500" data-testid="cc-exp-loading"><Loader2 className="inline animate-spin mr-2" size={14} /> Memuat data pembayaran…</div>;

  return (
    <div className="space-y-3" data-testid="cc-exp-tab">
      <p className="text-[11px] text-slate-500" data-testid="cc-exp-hint">
        Daftar pengeluaran proyek ini dari 2 sumber: mutasi <span className="font-semibold text-slate-700">Pembayaran</span> (Kas &amp; Bank → Buku Bank → Pembayaran — termasuk baris e-statement yang sudah dikategorikan di Input &amp; Categorize) dan realisasi <span className="font-semibold text-slate-700">Petty Cash</span> (Weekly Petty Cash maupun Catat Pengeluaran) yang sudah terverifikasi FAT. Klasifikasi yang tertaut ke WO/VO anak ikut tampil di sini.
        Alokasikan nominalnya ke baris <span className="font-semibold text-slate-700">Sub Item Pekerjaan</span> — expense item terisi otomatis dari total alokasi dan tidak bisa diisi manual.
      </p>

      {/* 2026-10-14 (user report): tooltip di tombol disabled tidak pernah tampil
          di browser — tampilkan penjelasan secara eksplisit saat tombol nonaktif. */}
      {totals && (
        <div className="border border-slate-200 bg-slate-50 px-3 py-2 space-y-1" data-testid="cc-exp-totals">
          <div className="text-[11px] text-slate-700" data-testid="cc-exp-list-scope">Cakupan daftar: <b>Seluruh pengeluaran {scope?.root_name || "proyek/outlet ini"}</b></div>
          {(scope?.scope_type === "work_order" || scope?.scope_type === "variation_order") && (
            <div className="text-[11px] text-sky-800" data-testid="cc-exp-alloc-context">Konteks alokasi aktif: <b>{scope.scope_type === "work_order" ? "WO" : "VO"} {scope.label}</b>. Daftar tetap menampilkan seluruh pengeluaran proyek agar transaksi dapat dialokasikan ke {scope.scope_type === "work_order" ? "WO" : "VO"} yang sesuai.</div>
          )}
          <div className="flex flex-wrap gap-4 text-[11px] text-slate-600">
            <span>Transaksi: <b className="text-[#0B1D3A]">{totals.count}</b></span>
            <span>Total nominal: <b className="text-[#0B1D3A] tabular-nums">{formatIDR(totals.amount)}</b></span>
            <span>Teralokasi: <b className="tabular-nums">{formatIDR(totals.allocated_total)}</b></span>
            <span>Sisa belum dialokasikan: <b className="text-amber-700 tabular-nums">{formatIDR(totals.remaining)}</b></span>
          </div>
          <div className="text-[10px] text-slate-500" data-testid="cc-exp-date-note">{TX_DATE_HINT}</div>
        </div>
      )}

      {subCount === 0 && (
        <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="cc-exp-no-sub-hint">
          Tombol <span className="font-semibold">Alokasikan</span> nonaktif karena proyek ini belum memiliki baris item pekerjaan sebagai target alokasi.
          Tambahkan dulu baris pekerjaan di tab <span className="font-semibold">Rincian Cost Control</span> (manual atau Import Excel) — baris tanpa No/Sub No pun kini bisa menjadi target alokasi.
        </div>
      )}

      {/* 2026-10-15 (user report): judul kolom hilang saat scroll — pakai
          SyncStickyTableHead agar header tabel tetap menempel di viewport. */}
      <SyncStickyTableHead offset="context" testid="cc-exp-table" className="border border-slate-200 bg-white">
        <table className="w-full text-xs min-w-[960px]">
          <thead>
            <tr className="text-left bg-slate-50 border-b-2 border-slate-300">
              <th className="px-2 py-2 overline text-slate-700 w-28" title={TX_DATE_HINT} data-testid="cc-exp-th-date">Tanggal Transaksi</th>
              <th className="px-2 py-2 overline text-slate-700">Deskripsi</th>
              <th className="px-2 py-2 overline text-slate-700 w-36">Rekening</th>
              <th className="px-2 py-2 overline text-slate-700 w-36">COA</th>
              <th className="px-2 py-2 overline text-slate-700 w-28 text-right">Nominal</th>
              <th className="px-2 py-2 overline text-slate-700 w-28 text-right">Teralokasi</th>
              <th className="px-2 py-2 overline text-slate-700 w-28 text-right">Sisa</th>
              <th className="px-2 py-2 overline text-slate-700 w-28 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody data-testid="cc-exp-tbody">
            {sources.map((s) => {
              const sisa = Number(s.remaining || 0);
              return (
                <tr key={s.movement_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cc-exp-row-${s.movement_id}`}>
                  <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap">{s.date ? String(s.date).slice(0, 10) : <span className="text-amber-700 italic text-[10px]">Tanggal transaksi belum tersedia</span>}</td>
                  <td className="px-2 py-1.5 text-slate-800 break-words">
                    {s.description || "—"}
                    {s.doc_no ? <span className="text-slate-400"> · {s.doc_no}</span> : null}
                    <div className="text-[10px] mt-0.5">
                      {s.origin === "manual_bank" ? (
                        <span className="inline-block px-1.5 py-px bg-sky-100 text-sky-800 font-semibold uppercase tracking-wider" data-testid={`cc-exp-src-manualbank-${s.movement_id}`}>
                          {s.origin_label}
                        </span>
                      ) : s.origin === "manual_expense" ? (
                        <span className="inline-flex flex-wrap gap-1">
                          <span className="inline-block px-1.5 py-px bg-orange-100 text-orange-800 font-semibold uppercase tracking-wider" data-testid={`cc-exp-src-manual-${s.movement_id}`}>
                            {s.origin_label} · {s.pc_staff || "—"}
                          </span>
                          {s.suggested_wo_number && (
                            <span className="inline-block px-1.5 py-px border border-slate-300 text-slate-600" data-testid={`cc-exp-suggest-wo-${s.movement_id}`}>
                              Saran tujuan: {String(s.suggested_wo_number).replace(/^MAIN:.*/, "Main").replace(/^VO:/, "VO ")}
                            </span>
                          )}
                        </span>
                      ) : s.source_type === "petty_cash" ? (
                        <span className="inline-block px-1.5 py-px bg-amber-100 text-amber-800 font-semibold uppercase tracking-wider" data-testid={`cc-exp-src-pc-${s.movement_id}`}>
                          Petty Cash · {s.pc_staff || "—"} · {s.week_label || "—"}
                        </span>
                      ) : (
                        <span className="text-slate-400">{s.source || ""}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-slate-600">{s.account_bank_name || "—"}{s.account_no ? ` · ${s.account_no}` : ""}</td>
                  <td className="px-2 py-1.5 text-slate-600">{s.coa_code ? `${s.coa_code} · ${s.coa_name || ""}` : "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-[#0B1D3A]">{formatIDR(s.amount)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{formatIDR(s.allocated_total)}</td>
                  <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${sisa > 0.01 ? "text-amber-700" : "text-emerald-700"}`} data-testid={`cc-exp-sisa-${s.movement_id}`}>{formatIDR(sisa)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {canEdit && (
                      <button type="button" onClick={() => setDlgFor(s)} disabled={subCount === 0}
                        title={subCount === 0 ? "Buat dulu baris Sub Item Pekerjaan di tab Rincian" : "Alokasikan ke item pekerjaan"}
                        data-testid={`cc-exp-alloc-${s.movement_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 border border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-40">
                        <Link2 size={11} /> Alokasikan
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {sources.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-[11px] text-slate-400 italic" data-testid="cc-exp-empty">
                {false ? null : <>Belum ada pengeluaran terkait proyek ini — klasifikasikan mutasi sebagai Pembayaran &amp; tautkan proyek di Kas &amp; Bank → Buku Bank (atau kategorikan baris e-statement di Input &amp; Categorize), atau verifikasi item petty cash proyek ini.</>}
              </td></tr>
            )}
          </tbody>
        </table>
      </SyncStickyTableHead>

      {dlgFor && (
        <AllocDialog pid={pid} mv={dlgFor} groups={groups} canEdit={canEdit} scope={scope}
          allocs={allocs.filter((a) => a.movement_id === dlgFor.movement_id)}
          onClose={() => setDlgFor(null)}
          onDone={async () => { setDlgFor(null); await load(); }} />
      )}
    </div>
  );
}

function AllocDialog({ pid, mv, groups, allocs, canEdit, onClose, onDone, scope }) {
  // 2026-06-21 (user request): split 1 transaksi ke beberapa item pekerjaan
  // sekaligus — daftar baris alokasi, nominal diisi manual per baris.
  const keyRef = useRef(2);
  const [rows, setRows] = useState([{ key: 1, itemId: "", amount: "" }]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [delBusy, setDelBusy] = useState(null);
  const remaining = Number(mv.remaining || 0);

  const totalNew = Math.round(rows.reduce((a, r) => a + parseNum(r.amount), 0) * 100) / 100;
  const over = totalNew > remaining + 0.01;
  const rowsValid = rows.length > 0 && rows.every((r) => r.itemId && parseNum(r.amount) > 0);
  const canSave = rowsValid && !over && !busy;

  const setRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => { setRows((rs) => [...rs, { key: keyRef.current++, itemId: "", amount: "" }]); };
  const removeRow = (key) => setRows((rs) => rs.filter((r) => r.key !== key));

  const save = async () => {
    if (!rowsValid) { toast.error("Lengkapi semua baris: pilih item pekerjaan dan isi nominal lebih dari 0."); return; }
    if (over) { toast.error(`Total alokasi melebihi sisa mutasi (${formatIDR(remaining)}).`); return; }
    setBusy(true);
    try {
      await api.post(`/active-projects/${pid}/cost-control/allocations/batch`, {
        movement_id: mv.movement_id,
        note,
        rows: rows.map((r) => ({ item_id: r.itemId, amount: parseNum(r.amount) })),
      });
      toast.success(`${rows.length} alokasi tersimpan — expense item diperbarui otomatis`);
      await onDone();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal menyimpan alokasi.");
      setBusy(false);
    }
  };

  const del = async (aid) => {
    setDelBusy(aid);
    try {
      await api.delete(`/active-projects/${pid}/cost-control/allocations/${aid}`);
      toast.success("Alokasi dihapus — expense item diperbarui otomatis");
      await onDone();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal menghapus alokasi.");
      setDelBusy(null);
    }
  };

  const inp = "w-full border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:border-[#0B1D3A] bg-white";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="cc-exp-dialog">
      <div className="bg-white w-full max-w-lg border border-slate-300 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <h3 className="text-sm font-bold text-[#0B1D3A] uppercase tracking-wider">Alokasikan Pembayaran</h3>
          <button type="button" onClick={onClose} data-testid="cc-exp-cancel" className="p-1 text-slate-400 hover:text-slate-700"><X size={15} /></button>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="border border-slate-200 bg-slate-50 p-2.5 text-xs space-y-0.5">
            <div className="font-semibold text-slate-800 break-words">{mv.description || "—"}</div>
            <div className="text-slate-500">Tanggal Transaksi: {mv.date ? String(mv.date).slice(0, 10) : "belum tersedia"} · {mv.account_bank_name || "—"}{mv.account_no ? ` · ${mv.account_no}` : ""}</div>
            {scope && (scope.scope_type === "work_order" || scope.scope_type === "variation_order") && (
              <div className="text-sky-800" data-testid="cc-exp-dlg-target">Target aktif: {scope.scope_type === "work_order" ? "WO" : "VO"} {scope.label} — pilihan item di bawah adalah sub-item Cost Control milik {scope.scope_type === "work_order" ? "WO" : "VO"} ini. Simpan hanya bila bukti transaksi memang milik {scope.scope_type === "work_order" ? "WO" : "VO"} ini.</div>
            )}
            <div className="flex gap-4 pt-1">
              <span>Nominal: <b className="tabular-nums text-[#0B1D3A]">{formatIDR(mv.amount)}</b></span>
              <span>Teralokasi: <b className="tabular-nums">{formatIDR(mv.allocated_total)}</b></span>
              <span>Sisa: <b className="tabular-nums text-amber-700" data-testid="cc-exp-dlg-sisa">{formatIDR(remaining)}</b></span>
            </div>
          </div>

          {allocs.length > 0 && (
            <div className="space-y-1" data-testid="cc-exp-alloc-list">
              <div className="overline text-slate-500">Alokasi saat ini</div>
              {allocs.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 border border-slate-200 px-2 py-1.5 text-xs" data-testid={`cc-exp-alloc-row-${a.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{a.item_label || a.item_id}</div>
                    {a.note ? <div className="text-[10px] text-slate-400 truncate">{a.note}</div> : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="tabular-nums font-semibold text-[#0B1D3A]">{formatIDR(a.amount)}</span>
                    {canEdit && (
                      <button type="button" onClick={() => del(a.id)} disabled={delBusy === a.id} title="Hapus alokasi"
                        data-testid={`cc-exp-del-${a.id}`}
                        className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 disabled:opacity-40">
                        {delBusy === a.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {canEdit && remaining > 0.01 && (
            <div className="space-y-2 border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between">
                <div className="overline text-slate-500">Alokasi baru</div>
                <button type="button" onClick={addRow} data-testid="cc-exp-add-row"
                  className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 bg-white hover:bg-slate-50 text-[10px] uppercase tracking-wider font-semibold text-slate-700">
                  <Plus size={11} /> Tambah Baris
                </button>
              </div>
              <p className="text-[10px] text-slate-500" data-testid="cc-exp-split-hint">
                Satu transaksi bisa dipecah ke beberapa item pekerjaan — tambahkan baris lalu isi nominalnya secara manual. Total semua baris tidak boleh melebihi sisa mutasi.
              </p>
              {rows.map((r, idx) => (
                <div key={r.key} className="border border-slate-200 p-2 space-y-1.5" data-testid={`cc-exp-split-row-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Baris {idx + 1}</span>
                    {rows.length > 1 && (
                      <button type="button" onClick={() => removeRow(r.key)} title="Hapus baris"
                        data-testid={`cc-exp-split-del-${idx}`}
                        className="p-0.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                  <select value={r.itemId} onChange={(e) => setRow(r.key, { itemId: e.target.value })} className={inp} data-testid={`cc-exp-split-item-${idx}`}>
                    <option value="">— Pilih item pekerjaan —</option>
                    {groups.map((g, gi) => (
                      <optgroup key={g.leader ? g.leader.id : `nogroup-${gi}`} label={`${g.scopeLabel ? g.scopeLabel + " · " : ""}${g.leader ? `${g.leader.no} — ${g.leader.description}` : "Tanpa Judul"}`}>
                        {g.subs.map((s) => (
                          <option key={s.id} value={s.id}>{s.sub_no ? `${s.sub_no} — ` : ""}{s.description}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 shrink-0">Nominal Alokasi *</span>
                    <input value={r.amount} onChange={(e) => setRow(r.key, { amount: e.target.value })} inputMode="decimal"
                      className={`${inp} text-right tabular-nums`} placeholder="0" data-testid={`cc-exp-split-amount-${idx}`} />
                  </div>
                </div>
              ))}
              <div className={`border px-2 py-1.5 text-xs flex justify-between ${over ? "border-rose-300 bg-rose-50 text-rose-700" : totalNew > 0 ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}
                data-testid="cc-exp-total-new">
                <span>Total alokasi baru: <b className="tabular-nums">{formatIDR(totalNew)}</b></span>
                <span>Sisa mutasi: <b className="tabular-nums">{formatIDR(remaining)}</b></span>
              </div>
              {over && (
                <p className="text-[11px] text-rose-700" data-testid="cc-exp-over-warning">
                  Total alokasi melebihi sisa mutasi sebesar {formatIDR(Math.round((totalNew - remaining) * 100) / 100)} — kurangi nominal baris.
                </p>
              )}
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Catatan</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} className={inp} placeholder="Opsional — berlaku untuk semua baris" data-testid="cc-exp-note" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onClose}
                  className="px-3 py-1.5 border border-slate-300 bg-white hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-700">
                  Batal
                </button>
                <button type="button" onClick={save} disabled={!canSave} data-testid="cc-exp-save"
                  className="px-3 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-50">
                  {busy ? "Menyimpan…" : `Simpan ${rows.length} Alokasi`}
                </button>
              </div>
            </div>
          )}
          {canEdit && remaining <= 0.01 && (
            <p className="text-[11px] text-emerald-700 italic border-t border-slate-200 pt-3" data-testid="cc-exp-full">Mutasi sudah teralokasi penuh.</p>
          )}
        </div>
      </div>
    </div>
  );
}
